'use client';

import React, { createContext, useContext, useState, useEffect, useMemo } from 'react';
import { useSocket } from './SocketContext';
import { getBannerAnnouncement } from '@/lib/api-client';
import { useVisibility } from '@/lib/hooks/useVisibility';

interface Announcement {
  id: string;
  message: string;
  icon?: string;
  highlightColor?: string;
  link?: string;
  linkText?: string;
  startTime?: string;
  endTime?: string;
}

interface AnnouncementContextType {
  bannerAnnouncement: Announcement | null;
  chatAnnouncement: Announcement | null;
}

const AnnouncementContext = createContext<AnnouncementContextType>({
  bannerAnnouncement: null,
  chatAnnouncement: null,
});

export const useAnnouncement = () => useContext(AnnouncementContext);

export function AnnouncementProvider({ children }: { children: React.ReactNode }) {
  const { socket, isConnected } = useSocket();
  const [bannerAnnouncement, setBannerAnnouncement] = useState<Announcement | null>(null);
  const [chatAnnouncement, setChatAnnouncement] = useState<Announcement | null>(null);
  const isVisible = useVisibility();

  // Fetch banner announcement on mount (HTTP API) - pause when tab is hidden
  useEffect(() => {
    if (!isVisible) return;

    const fetchBannerAnnouncement = async () => {
      try {
        const response = await getBannerAnnouncement();
        if (response.success && response.announcement) {
          setBannerAnnouncement(response.announcement);
        } else if (response.success && !response.announcement) {
          // No active banner announcement
          setBannerAnnouncement(null);
        }
      } catch (error) {
        // Silent error handling
      }
    };

    fetchBannerAnnouncement();

    // Refresh every 5 minutes
    const interval = setInterval(fetchBannerAnnouncement, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [isVisible]);

  // Listen for announcements via Socket.IO
  useEffect(() => {
    if (!socket || !isConnected) return;

    // Listen for new chat announcements
    const handleChatAnnouncement = (data: Announcement) => {
      setChatAnnouncement(data);
    };

    // Listen for chat announcement removal
    const handleChatAnnouncementRemoved = (data: { id: string }) => {
      setChatAnnouncement((current) => {
        if (current && current.id === data.id) {
          return null;
        }
        return current;
      });
    };

    // Listen for new banner announcements
    const handleBannerAnnouncement = (data: Announcement) => {
      setBannerAnnouncement(data);
    };

    // Listen for banner announcement removal
    const handleBannerAnnouncementRemoved = (data: { id: string }) => {
      setBannerAnnouncement((current) => {
        if (current && current.id === data.id) {
          return null;
        }
        return current;
      });
    };

    // Register all listeners
    socket.on('chat:special-announcement', handleChatAnnouncement);
    socket.on('chat:special-announcement:removed', handleChatAnnouncementRemoved);
    socket.on('banner:announcement', handleBannerAnnouncement);
    socket.on('banner:announcement:removed', handleBannerAnnouncementRemoved);

    // Request current announcements on connect
    socket.emit('announcements:request');

    return () => {
      socket.off('chat:special-announcement', handleChatAnnouncement);
      socket.off('chat:special-announcement:removed', handleChatAnnouncementRemoved);
      socket.off('banner:announcement', handleBannerAnnouncement);
      socket.off('banner:announcement:removed', handleBannerAnnouncementRemoved);
    };
  }, [socket, isConnected]);

  // Auto-remove banner announcement when it expires
  useEffect(() => {
    if (!bannerAnnouncement || !bannerAnnouncement.endTime) return;

    const endTime = bannerAnnouncement.endTime;
    const announcementId = bannerAnnouncement.id;

    const checkExpiry = () => {
      const now = new Date().getTime();
      const end = new Date(endTime).getTime();
      
      if (now >= end) {
        setBannerAnnouncement((current) => {
          // Only remove if it's still the same announcement
          if (current && current.id === announcementId) {
            return null;
          }
          return current;
        });
      }
    };

    checkExpiry();
    const interval = setInterval(checkExpiry, 1000);

    return () => clearInterval(interval);
  }, [bannerAnnouncement?.id, bannerAnnouncement?.endTime]);

  // Auto-remove chat announcement when it expires
  useEffect(() => {
    if (!chatAnnouncement || !chatAnnouncement.endTime) return;

    const endTime = chatAnnouncement.endTime;
    const announcementId = chatAnnouncement.id;

    const checkExpiry = () => {
      const now = new Date().getTime();
      const end = new Date(endTime).getTime();
      
      if (now >= end) {
        setChatAnnouncement((current) => {
          // Only remove if it's still the same announcement
          if (current && current.id === announcementId) {
            return null;
          }
          return current;
        });
      }
    };

    checkExpiry();
    const interval = setInterval(checkExpiry, 1000);

    return () => clearInterval(interval);
  }, [chatAnnouncement?.id, chatAnnouncement?.endTime]);

  const value = useMemo(() => ({ bannerAnnouncement, chatAnnouncement }), [bannerAnnouncement, chatAnnouncement]);

  return (
    <AnnouncementContext.Provider value={value}>
      {children}
    </AnnouncementContext.Provider>
  );
}

